import { useEffect, useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import type { DateSelectArg, DatesSetArg, EventClickArg, EventInput } from '@fullcalendar/core';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  IconButton,
  Typography,
  Chip,
  Drawer,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Stack,
  Pagination,
  InputAdornment,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  CalendarToday as CalendarIcon,
  ViewList as ListIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Close as CloseIcon,
  CheckCircle as ConfirmIcon,
  DoneAll as CompleteIcon,
  Cancel as CancelIcon,
  NoAccounts as NoShowIcon,
} from '@mui/icons-material';
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  updateDoc,
  doc,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { useTenant } from '../providers/TenantProvider';
import type { Appointment, AppointmentStatus, Service, User, Member } from '../lib/db/types';
import { appointmentConverter } from '../lib/db/converters';
import { useNavigate } from 'react-router-dom';

// Types
type ViewMode = 'list' | 'calendar';
type SortField = 'startTime' | 'service' | 'provider' | 'customer' | 'status';
type SortDirection = 'asc' | 'desc';

interface AppointmentWithId extends Appointment {
  id: string;
}

interface ServiceWithId extends Service {
  id: string;
}

interface UserWithId extends User {
  id: string;
}

interface MemberWithId extends Member {
  id: string;
}

interface Filters {
  dateFrom: string;
  dateTo: string;
  providerId: string;
  serviceId: string;
  status: AppointmentStatus | 'all';
  search: string;
}

function isIndexBuildingError(error: any) {
  const message = String(error?.message || '');
  return error?.code === 'failed-precondition' && message.includes('requires an index');
}

function timestampMillis(value: any) {
  if (value?.toMillis) {
    return value.toMillis();
  }
  if (value?.toDate) {
    return value.toDate().getTime();
  }
  return 0;
}

function toDateInputValue(date: Date) {
  const offset = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function toDateOnlyValue(date: Date) {
  const offset = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
}

// Status config
const STATUS_CONFIG: Record<AppointmentStatus, { label: string; color: 'success' | 'warning' | 'error' | 'info' | 'default' }> = {
  confirmed: { label: 'Confirmed', color: 'success' },
  pending: { label: 'Pending', color: 'warning' },
  completed: { label: 'Completed', color: 'info' },
  canceled: { label: 'Canceled', color: 'error' },
  no_show: { label: 'No Show', color: 'default' },
};

// Helper functions
function formatDateTime(timestamp: Timestamp): string {
  if (!timestamp?.toDate) return '—';
  return timestamp.toDate().toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDate(timestamp: Timestamp): string {
  if (!timestamp?.toDate) return '—';
  return timestamp.toDate().toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function formatTime(timestamp: Timestamp): string {
  if (!timestamp?.toDate) return '—';
  return timestamp.toDate().toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getCustomerDisplayName(appointment: Appointment, member?: MemberWithId): string {
  if (appointment.customerType === 'member' && member) {
    return `${member.contact.firstName} ${member.contact.lastName}`;
  }
  if (appointment.guestInfo) {
    return `${appointment.guestInfo.firstName} ${appointment.guestInfo.lastName}`;
  }
  return 'Unknown';
}

function getCustomerEmail(appointment: Appointment, member?: MemberWithId): string | undefined {
  if (appointment.customerType === 'member' && member) {
    return member.contact.email;
  }
  return appointment.guestInfo?.email;
}

function getCustomerPhone(appointment: Appointment, member?: MemberWithId): string | undefined {
  if (appointment.customerType === 'member' && member) {
    return member.contact.phone;
  }
  return appointment.guestInfo?.phone;
}

export function AppointmentsPage() {
  const { tenantId, role } = useTenant();
  const navigate = useNavigate();

  // State
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [appointments, setAppointments] = useState<AppointmentWithId[]>([]);
  const [services, setServices] = useState<ServiceWithId[]>([]);
  const [providers, setProviders] = useState<UserWithId[]>([]);
  const [members, setMembers] = useState<MemberWithId[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Sorting
  const [sortField, setSortField] = useState<SortField>('startTime');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Pagination
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  // Filters
  const today = new Date().toISOString().split('T')[0];
  const nextWeek = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const [filters, setFilters] = useState<Filters>({
    dateFrom: today,
    dateTo: nextWeek,
    providerId: 'all',
    serviceId: 'all',
    status: 'all',
    search: '',
  });
    const [_showFilters, _setShowFilters] = useState(false);

  // Drawer state
  const [selectedAppointment, setSelectedAppointment] = useState<AppointmentWithId | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    startTime: '',
    endTime: '',
    serviceId: '',
    providerId: '',
    customerType: 'member' as 'member' | 'guest',
    customerId: '',
    guestFirstName: '',
    guestLastName: '',
    guestEmail: '',
    guestPhone: '',
    notes: '',
  });

  const canWrite = role === 'owner' || role === 'manager' || role === 'staff';

  // Load data
  useEffect(() => {
    if (!tenantId) return;
    loadData();
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) return;
    loadAppointments();
  }, [tenantId, filters.dateFrom, filters.dateTo]);

  async function loadData() {
    if (!tenantId) return;

    try {
      setLoading(true);
      setMessage(null);

      // Load services
      const servicesQuery = query(
        collection(db, 'services'),
        where('tenantId', '==', tenantId),
        where('isActive', '==', true),
        orderBy('name')
      );
      const servicesSnap = await getDocs(servicesQuery);
      const servicesData = servicesSnap.docs.map((d) => ({ id: d.id, ...d.data() })) as ServiceWithId[];
      setServices(servicesData);

      // Load providers (users with coach or staff role)
      const providersQuery = query(
        collection(db, 'tenants', tenantId, 'memberships'),
        where('roles', 'array-contains-any', ['owner', 'manager', 'staff', 'coach'])
      );
      const providersSnap = await getDocs(providersQuery);
      const providersData = providersSnap.docs.map((d) => {
        const data = d.data();
        return { id: d.id, ...data, role: data.primaryRole } as UserWithId;
      });
      setProviders(providersData);

      // Load members
      const membersQuery = query(
        collection(db, 'members'),
        where('tenantId', '==', tenantId),
        orderBy('contact.firstName')
      );
      const membersSnap = await getDocs(membersQuery);
      const membersData = membersSnap.docs.map((d) => ({ id: d.id, ...d.data() })) as MemberWithId[];
      setMembers(membersData);

      // Load appointments with date range filter
      await loadAppointments();
    } catch (err: any) {
      console.error('Error loading data:', err);
      setMessage({ type: 'error', text: err.message || 'Failed to load data' });
    } finally {
      setLoading(false);
    }
  }

  async function loadAppointments() {
    if (!tenantId) return;

    try {
      const startOfDay = Timestamp.fromDate(new Date(filters.dateFrom + 'T00:00:00'));
      const endOfDay = Timestamp.fromDate(new Date(filters.dateTo + 'T23:59:59'));

      const appointmentsQuery = query(
        collection(db, 'appointments').withConverter(appointmentConverter),
        where('tenantId', '==', tenantId),
        where('startTime', '>=', startOfDay),
        where('startTime', '<=', endOfDay),
        orderBy('startTime')
      );

      let appointmentsSnap;
      try {
        appointmentsSnap = await getDocs(appointmentsQuery);
      } catch (queryError) {
        if (!isIndexBuildingError(queryError)) {
          throw queryError;
        }

        appointmentsSnap = await getDocs(
          query(
            collection(db, 'appointments').withConverter(appointmentConverter),
            where('tenantId', '==', tenantId)
          )
        );
      }
      const appointmentsData = appointmentsSnap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as AppointmentWithId[];

      setAppointments(
        appointmentsData
          .filter((appointment) => {
            const startMillis = timestampMillis(appointment.startTime);
            return startMillis >= startOfDay.toMillis() && startMillis <= endOfDay.toMillis();
          })
          .sort((a, b) => timestampMillis(a.startTime) - timestampMillis(b.startTime))
      );
    } catch (err: any) {
      console.error('Error loading appointments:', err);
      setMessage({ type: 'error', text: err.message || 'Failed to load appointments' });
    }
  }

  // Filter and sort appointments
  const filteredAppointments = useMemo(() => {
    let result = [...appointments];

    // Provider filter
    if (filters.providerId !== 'all') {
      result = result.filter((a) => a.providerId === filters.providerId);
    }

    // Service filter
    if (filters.serviceId !== 'all') {
      result = result.filter((a) => a.serviceId === filters.serviceId);
    }

    // Status filter
    if (filters.status !== 'all') {
      result = result.filter((a) => a.status === filters.status);
    }

    // Search filter
    if (filters.search) {
      const term = filters.search.toLowerCase();
      result = result.filter((a) => {
        const member = members.find((m) => m.id === a.customerId);
        const customerName = getCustomerDisplayName(a, member).toLowerCase();
        const service = services.find((s) => s.id === a.serviceId);
        const serviceName = service?.name?.toLowerCase() || '';
        const provider = providers.find((p) => p.id === a.providerId);
        const providerName = `${provider?.firstName || ''} ${provider?.lastName || ''}`.toLowerCase();
        return customerName.includes(term) || serviceName.includes(term) || providerName.includes(term);
      });
    }

    // Sort
    result.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'startTime':
          comparison = a.startTime.toMillis() - b.startTime.toMillis();
          break;
        case 'service':
          const serviceA = services.find((s) => s.id === a.serviceId)?.name || '';
          const serviceB = services.find((s) => s.id === b.serviceId)?.name || '';
          comparison = serviceA.localeCompare(serviceB);
          break;
        case 'provider':
          const providerA = providers.find((p) => p.id === a.providerId);
          const providerB = providers.find((p) => p.id === b.providerId);
          const nameA = `${providerA?.firstName || ''} ${providerA?.lastName || ''}`;
          const nameB = `${providerB?.firstName || ''} ${providerB?.lastName || ''}`;
          comparison = nameA.localeCompare(nameB);
          break;
        case 'customer':
          const memberA = members.find((m) => m.id === a.customerId);
          const memberB = members.find((m) => m.id === b.customerId);
          const custA = getCustomerDisplayName(a, memberA);
          const custB = getCustomerDisplayName(b, memberB);
          comparison = custA.localeCompare(custB);
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [appointments, filters, sortField, sortDirection, services, providers, members]);

  // Paginate
  const paginatedAppointments = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredAppointments.slice(start, start + rowsPerPage);
  }, [filteredAppointments, page]);

  const totalPages = Math.ceil(filteredAppointments.length / rowsPerPage);

  // Handlers
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleFilterChange = (key: keyof Filters, value: string) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({
      dateFrom: today,
      dateTo: nextWeek,
      providerId: 'all',
      serviceId: 'all',
      status: 'all',
      search: '',
    });
    setPage(1);
  };

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.providerId !== 'all') count++;
    if (filters.serviceId !== 'all') count++;
    if (filters.status !== 'all') count++;
    if (filters.search) count++;
    if (filters.dateFrom !== today || filters.dateTo !== nextWeek) count++;
    return count;
  }, [filters, today, nextWeek]);

  const openAppointmentDetails = (appointment: AppointmentWithId) => {
    setSelectedAppointment(appointment);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedAppointment(null);
  };

  const updateAppointmentStatus = async (appointmentId: string, status: AppointmentStatus) => {
    try {
      setMessage(null);
      await updateDoc(doc(db, 'appointments', appointmentId), {
        status,
        updatedAt: Timestamp.now(),
      });

      // Update local state
      setAppointments((prev) =>
        prev.map((a) => (a.id === appointmentId ? { ...a, status } : a))
      );

      if (selectedAppointment?.id === appointmentId) {
        setSelectedAppointment({ ...selectedAppointment, status });
      }

      setMessage({ type: 'success', text: `Appointment ${status.replace('_', ' ')}` });
    } catch (err: any) {
      console.error('Error updating status:', err);
      setMessage({ type: 'error', text: err.message || 'Failed to update status' });
    }
  };

  const openCreateDialog = () => {
    setCreateForm({
      startTime: today + 'T09:00',
      endTime: today + 'T10:00',
      serviceId: services[0]?.id || '',
      providerId: providers[0]?.id || '',
      customerType: 'member',
      customerId: members[0]?.id || '',
      guestFirstName: '',
      guestLastName: '',
      guestEmail: '',
      guestPhone: '',
      notes: '',
    });
    setCreateDialogOpen(true);
  };

  const closeCreateDialog = () => {
    setCreateDialogOpen(false);
  };

  const handleCreateAppointment = async () => {
    if (!tenantId) return;

    try {
      setMessage(null);

      // Validation
      if (!createForm.serviceId || !createForm.providerId || !createForm.startTime || !createForm.endTime) {
        setMessage({ type: 'error', text: 'Please fill in all required fields' });
        return;
      }

      if (createForm.customerType === 'member' && !createForm.customerId) {
        setMessage({ type: 'error', text: 'Please select a member' });
        return;
      }

      if (createForm.customerType === 'guest' && (!createForm.guestFirstName || !createForm.guestLastName || !createForm.guestEmail)) {
        setMessage({ type: 'error', text: 'Please fill in guest information' });
        return;
      }

      const bookAppointment = httpsCallable(functions, 'bookAppointment');
      await bookAppointment({
        tenantId,
        serviceId: createForm.serviceId,
        providerId: createForm.providerId,
        startTime: new Date(createForm.startTime).toISOString(),
        customerType: createForm.customerType,
        customerId: createForm.customerType === 'member' ? createForm.customerId : undefined,
        guestInfo: createForm.customerType === 'guest' ? {
          firstName: createForm.guestFirstName,
          lastName: createForm.guestLastName,
          email: createForm.guestEmail,
          phone: createForm.guestPhone || undefined,
        } : undefined,
        source: 'admin_booking',
        notes: createForm.notes || undefined,
      });

      setMessage({ type: 'success', text: 'Appointment created successfully' });
      closeCreateDialog();
      await loadAppointments();
    } catch (err: any) {
      console.error('Error creating appointment:', err);
      setMessage({ type: 'error', text: err.message || 'Failed to create appointment' });
    }
  };

  // Get helper data
  const getService = (id: string) => services.find((s) => s.id === id);
  const getProvider = (id: string) => providers.find((p) => p.id === id);
  const getMember = (id?: string) => members.find((m) => m.id === id);

  const calendarEvents = useMemo<EventInput[]>(
    () =>
      filteredAppointments.map((appointment) => {
        const service = getService(appointment.serviceId);
        const member = getMember(appointment.customerId);
        const customerName = getCustomerDisplayName(appointment, member);
        const statusConfig = STATUS_CONFIG[appointment.status];
        return {
          id: appointment.id,
          title: `${customerName} - ${service?.name || 'Appointment'}`,
          start: appointment.startTime.toDate(),
          end: appointment.endTime.toDate(),
          backgroundColor: service?.color || undefined,
          borderColor: service?.color || undefined,
          extendedProps: {
            status: statusConfig.label,
            appointment,
          },
        };
      }),
    [filteredAppointments, services, members]
  );

  const handleCalendarDatesSet = (arg: DatesSetArg) => {
    const end = new Date(arg.end);
    end.setDate(end.getDate() - 1);
    const nextDateFrom = toDateOnlyValue(arg.start);
    const nextDateTo = toDateOnlyValue(end);
    setFilters((current) =>
      current.dateFrom === nextDateFrom && current.dateTo === nextDateTo
        ? current
        : { ...current, dateFrom: nextDateFrom, dateTo: nextDateTo }
    );
  };

  const handleCalendarSelect = (arg: DateSelectArg) => {
    if (!canWrite) return;
    const start = arg.start;
    const end = arg.end && arg.end.getTime() > arg.start.getTime()
      ? arg.end
      : new Date(arg.start.getTime() + 60 * 60 * 1000);

    setCreateForm({
      startTime: toDateInputValue(start),
      endTime: toDateInputValue(end),
      serviceId: services[0]?.id || '',
      providerId: providers[0]?.id || '',
      customerType: 'member',
      customerId: members[0]?.id || '',
      guestFirstName: '',
      guestLastName: '',
      guestEmail: '',
      guestPhone: '',
      notes: '',
    });
    setCreateDialogOpen(true);
  };

  const handleCalendarEventClick = (arg: EventClickArg) => {
    const appointment = arg.event.extendedProps.appointment as AppointmentWithId | undefined;
    if (appointment) openAppointmentDetails(appointment);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" component="h1">
          Appointments
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
            size="small"
          >
            <ToggleButton value="list">
              <ListIcon sx={{ mr: 0.5 }} fontSize="small" />
              List
            </ToggleButton>
            <ToggleButton value="calendar">
              <CalendarIcon sx={{ mr: 0.5 }} fontSize="small" />
              Calendar
            </ToggleButton>
          </ToggleButtonGroup>
          {canWrite && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
              Create
            </Button>
          )}
        </Box>
      </Box>

      {/* Messages */}
      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* Filters Bar */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              placeholder="Search appointments..."
              size="small"
              value={filters.search}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleFilterChange('search', e.target.value)}
              sx={{ minWidth: 200, flex: 1, maxWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FilterListIcon />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              type="date"
              size="small"
              label="From"
              value={filters.dateFrom}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleFilterChange('dateFrom', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              type="date"
              size="small"
              label="To"
              value={filters.dateTo}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleFilterChange('dateTo', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              select
              size="small"
              label="Provider"
              value={filters.providerId}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleFilterChange('providerId', e.target.value)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="all">All Providers</MenuItem>
              {providers.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.firstName || p.lastName ? `${p.firstName || ''} ${p.lastName || ''}`.trim() : p.email}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Service"
              value={filters.serviceId}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleFilterChange('serviceId', e.target.value)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="all">All Services</MenuItem>
              {services.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Status"
              value={filters.status}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleFilterChange('status', e.target.value)}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="all">All Status</MenuItem>
              {Object.entries(STATUS_CONFIG).map(([status, config]) => (
                <MenuItem key={status} value={status}>
                  {config.label}
                </MenuItem>
              ))}
            </TextField>

            {activeFiltersCount > 0 && (
              <Button startIcon={<ClearIcon />} size="small" onClick={clearFilters}>
                Clear ({activeFiltersCount})
              </Button>
            )}
          </Box>

          {/* Active Filter Chips */}
          {activeFiltersCount > 0 && (
            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {filters.providerId !== 'all' && (
                <Chip
                  size="small"
                  label={`Provider: ${getProvider(filters.providerId)?.firstName || 'Unknown'}`}
                  onDelete={() => handleFilterChange('providerId', 'all')}
                />
              )}
              {filters.serviceId !== 'all' && (
                <Chip
                  size="small"
                  label={`Service: ${getService(filters.serviceId)?.name || 'Unknown'}`}
                  onDelete={() => handleFilterChange('serviceId', 'all')}
                />
              )}
              {filters.status !== 'all' && (
                <Chip
                  size="small"
                  label={`Status: ${STATUS_CONFIG[filters.status as AppointmentStatus].label}`}
                  onDelete={() => handleFilterChange('status', 'all')}
                />
              )}
              {filters.search && (
                <Chip size="small" label={`Search: ${filters.search}`} onDelete={() => handleFilterChange('search', '')} />
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Results Count */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Showing {filteredAppointments.length} appointment{filteredAppointments.length !== 1 ? 's' : ''}
      </Typography>

      {/* List View */}
      {viewMode === 'list' && (
        <>
          <TableContainer component={Card}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'startTime'}
                      direction={sortDirection}
                      onClick={() => handleSort('startTime')}
                    >
                      Date/Time
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'service'}
                      direction={sortDirection}
                      onClick={() => handleSort('service')}
                    >
                      Service
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'provider'}
                      direction={sortDirection}
                      onClick={() => handleSort('provider')}
                    >
                      Provider
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'customer'}
                      direction={sortDirection}
                      onClick={() => handleSort('customer')}
                    >
                      Customer
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'status'}
                      direction={sortDirection}
                      onClick={() => handleSort('status')}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedAppointments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="text.secondary" sx={{ py: 4 }}>
                        No appointments found for the selected filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedAppointments.map((appointment) => {
                    const service = getService(appointment.serviceId);
                    const provider = getProvider(appointment.providerId);
                    const member = getMember(appointment.customerId);
                    const customerName = getCustomerDisplayName(appointment, member);
                    const statusConfig = STATUS_CONFIG[appointment.status];

                    return (
                      <TableRow
                        key={appointment.id}
                        hover
                        onClick={() => openAppointmentDetails(appointment)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {formatDate(appointment.startTime)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatTime(appointment.startTime)} - {formatTime(appointment.endTime)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {service?.color && (
                              <Box
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: '50%',
                                  bgcolor: service.color,
                                }}
                              />
                            )}
                            <Typography variant="body2">{service?.name || 'Unknown Service'}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PersonIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              {provider?.firstName || provider?.lastName
                                ? `${provider?.firstName || ''} ${provider?.lastName || ''}`.trim()
                                : provider?.email || 'Unknown'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{customerName}</Typography>
                          {appointment.customerType === 'guest' && (
                            <Chip size="small" label="Guest" sx={{ mt: 0.5 }} />
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" color={statusConfig.color} label={statusConfig.label} />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} />
            </Box>
          )}
        </>
      )}

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <Card>
          <CardContent>
            <Box
              sx={{
                '.fc': {
                  fontFamily: 'inherit',
                },
                '.fc .fc-toolbar': {
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                },
                '.fc .fc-button': {
                  bgcolor: 'primary.main',
                  borderColor: 'primary.main',
                  textTransform: 'none',
                  '&:hover': { bgcolor: 'primary.dark', borderColor: 'primary.dark' },
                  '&:disabled': { bgcolor: 'action.disabledBackground', borderColor: 'divider' },
                },
                '.fc .fc-button-primary:not(:disabled).fc-button-active': {
                  bgcolor: 'primary.dark',
                  borderColor: 'primary.dark',
                },
                '.fc .fc-event': {
                  borderRadius: 1,
                  cursor: 'pointer',
                  fontSize: 12,
                },
                '.fc .fc-timegrid-slot': {
                  height: 36,
                },
              }}
            >
              <FullCalendar
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                initialView="timeGridWeek"
                initialDate={filters.dateFrom}
                headerToolbar={{
                  left: 'prev,next today',
                  center: 'title',
                  right: 'dayGridMonth,timeGridWeek,timeGridDay',
                }}
                height="auto"
                nowIndicator
                selectable={canWrite}
                selectMirror
                events={calendarEvents}
                datesSet={handleCalendarDatesSet}
                select={handleCalendarSelect}
                eventClick={handleCalendarEventClick}
                eventContent={(eventInfo) => (
                  <Box sx={{ lineHeight: 1.25, overflow: 'hidden' }}>
                    <Box component="span" sx={{ fontWeight: 700, display: 'block' }}>
                      {eventInfo.timeText}
                    </Box>
                    <Box component="span">{eventInfo.event.title}</Box>
                  </Box>
                )}
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Appointment Detail Drawer */}
      <Drawer anchor="right" open={drawerOpen} onClose={closeDrawer} PaperProps={{ sx: { width: { xs: '100%', sm: 450 } } }}>
        {selectedAppointment && (
          <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
              <Typography variant="h5">Appointment Details</Typography>
              <IconButton onClick={closeDrawer} size="small">
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Status */}
            <Box sx={{ mb: 3 }}>
              <Chip
                size="medium"
                color={STATUS_CONFIG[selectedAppointment.status].color}
                label={STATUS_CONFIG[selectedAppointment.status].label}
                sx={{ fontSize: '1rem', py: 0.5 }}
              />
            </Box>

            {/* Status Actions */}
            {canWrite && selectedAppointment.status !== 'canceled' && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Update Status
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {selectedAppointment.status !== 'confirmed' && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      startIcon={<ConfirmIcon />}
                      onClick={() => updateAppointmentStatus(selectedAppointment.id, 'confirmed')}
                    >
                      Confirm
                    </Button>
                  )}
                  {selectedAppointment.status !== 'completed' && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="info"
                      startIcon={<CompleteIcon />}
                      onClick={() => updateAppointmentStatus(selectedAppointment.id, 'completed')}
                    >
                      Complete
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<CancelIcon />}
                    onClick={() => updateAppointmentStatus(selectedAppointment.id, 'canceled')}
                  >
                    Cancel
                  </Button>
                  {selectedAppointment.status !== 'no_show' && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<NoShowIcon />}
                      onClick={() => updateAppointmentStatus(selectedAppointment.id, 'no_show')}
                    >
                      No Show
                    </Button>
                  )}
                </Box>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Appointment Info */}
            <Typography variant="h6" gutterBottom>
              Appointment Info
            </Typography>

            <Stack spacing={2} sx={{ mb: 3 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  <TimeIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                  Date & Time
                </Typography>
                <Typography variant="body1">
                  {formatDateTime(selectedAppointment.startTime)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Duration: {Math.round((selectedAppointment.endTime.toMillis() - selectedAppointment.startTime.toMillis()) / 60000)} minutes
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Service
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getService(selectedAppointment.serviceId)?.color && (
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: getService(selectedAppointment.serviceId)?.color,
                      }}
                    />
                  )}
                  <Typography variant="body1">{getService(selectedAppointment.serviceId)?.name || 'Unknown'}</Typography>
                </Box>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  <PersonIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                  Provider
                </Typography>
                <Typography variant="body1">
                  {(() => {
                    const provider = getProvider(selectedAppointment.providerId);
                    return provider?.firstName || provider?.lastName
                      ? `${provider?.firstName || ''} ${provider?.lastName || ''}`.trim()
                      : provider?.email || 'Unknown';
                  })()}
                </Typography>
              </Box>

              {selectedAppointment.notes && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Notes
                  </Typography>
                  <Typography variant="body1">{selectedAppointment.notes}</Typography>
                </Box>
              )}
            </Stack>

            <Divider sx={{ my: 2 }} />

            {/* Customer Info */}
            <Typography variant="h6" gutterBottom>
              Customer Info
            </Typography>

            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Name
                </Typography>
                <Typography variant="body1">
                  {getCustomerDisplayName(selectedAppointment, getMember(selectedAppointment.customerId))}
                </Typography>
                {selectedAppointment.customerType === 'guest' ? (
                  <Chip size="small" label="Guest Customer" sx={{ mt: 0.5 }} />
                ) : (
                  <Chip size="small" label="Member" color="primary" sx={{ mt: 0.5 }} />
                )}
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  <EmailIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                  Email
                </Typography>
                <Typography variant="body1">
                  {getCustomerEmail(selectedAppointment, getMember(selectedAppointment.customerId)) || '—'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  <PhoneIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                  Phone
                </Typography>
                <Typography variant="body1">
                  {getCustomerPhone(selectedAppointment, getMember(selectedAppointment.customerId)) || '—'}
                </Typography>
              </Box>

              {selectedAppointment.customerType === 'member' && selectedAppointment.customerId && (
                <Box sx={{ mt: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate(`/members/${selectedAppointment.customerId}`)}
                  >
                    View Member Profile
                  </Button>
                </Box>
              )}
            </Stack>

            {/* Payment Info */}
            {selectedAppointment.paymentStatus && selectedAppointment.paymentStatus !== 'not_required' && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Payment
                </Typography>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Payment Status
                  </Typography>
                  <Typography variant="body1">{selectedAppointment.paymentStatus}</Typography>
                </Box>
              </>
            )}
          </Box>
        )}
      </Drawer>

      {/* Create Appointment Dialog */}
      <Dialog open={createDialogOpen} onClose={closeCreateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Create Appointment</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Start Time"
                  type="datetime-local"
                  fullWidth
                  value={createForm.startTime}
                  onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setCreateForm({ ...createForm, startTime: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="End Time"
                  type="datetime-local"
                  fullWidth
                  value={createForm.endTime}
                  onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setCreateForm({ ...createForm, endTime: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
            </Grid>

            <TextField
              select
              label="Service"
              fullWidth
              value={createForm.serviceId}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setCreateForm({ ...createForm, serviceId: e.target.value })}
              required
            >
              {services.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Provider"
              fullWidth
              value={createForm.providerId}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setCreateForm({ ...createForm, providerId: e.target.value })}
              required
            >
              {providers.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.firstName || p.lastName ? `${p.firstName || ''} ${p.lastName || ''}`.trim() : p.email}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Customer Type"
              fullWidth
              value={createForm.customerType}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setCreateForm({ ...createForm, customerType: e.target.value as 'member' | 'guest' })}
            >
              <MenuItem value="member">Member</MenuItem>
              <MenuItem value="guest">Guest</MenuItem>
            </TextField>

            {createForm.customerType === 'member' ? (
              <TextField
                select
                label="Select Member"
                fullWidth
                value={createForm.customerId}
                onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setCreateForm({ ...createForm, customerId: e.target.value })}
                required
              >
                {members.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.contact.firstName} {m.contact.lastName} ({m.contact.email})
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label="First Name"
                      fullWidth
                      value={createForm.guestFirstName}
                      onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setCreateForm({ ...createForm, guestFirstName: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Last Name"
                      fullWidth
                      value={createForm.guestLastName}
                      onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setCreateForm({ ...createForm, guestLastName: e.target.value })}
                      required
                    />
                  </Grid>
                </Grid>
                <TextField
                  label="Email"
                  type="email"
                  fullWidth
                  value={createForm.guestEmail}
                  onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setCreateForm({ ...createForm, guestEmail: e.target.value })}
                  required
                />
                <TextField
                  label="Phone (optional)"
                  fullWidth
                  value={createForm.guestPhone}
                  onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setCreateForm({ ...createForm, guestPhone: e.target.value })}
                />
              </>
            )}

            <TextField
              label="Notes (optional)"
              multiline
              rows={3}
              fullWidth
              value={createForm.notes}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setCreateForm({ ...createForm, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateDialog}>Cancel</Button>
          <Button onClick={handleCreateAppointment} variant="contained">
            Create Appointment
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
